import { NextPage } from "next"
import { useRouter } from "next/router"
import React, { useCallback, useEffect, useState } from "react"
import useSWR from "swr"
import axios from "axios"
import { ArtworkGallery } from "../../../components/artwork"
import { DEFAULT_PAGE_SIZE } from "../../../constants/application-settings"
import { ArtworkFilter } from "../../../components/artwork-filter"
import { ErrorView, LoadingView } from "../../../components/feedback"
import { FilterOptions } from "../../../components/artwork-filter/artwork-filter"
import { IArtwork } from "../../../interfaces/models"
import _ from "lodash"
import { Seo } from "../../../components/seo"
import { useTranslation } from "react-i18next"

const SearchPage: NextPage = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [canLoadMore, setCanLoadMore] = useState(false)

  const [queryParams, setQueryParams] = useState({})

  const [fetchedData, setFetchedData] = useState<
    IArtwork[] | undefined | null
  >()

  const { data, error, isValidating } = useSWR(
    [
      `/api/v1/search`,
      {
        ...queryParams,
        offset,
        limit,
      },
    ],
    (url, params) => {
      return axios.get(url, { params }).then(res => res.data)
    }
  )

  const handleLoadMore = () => {
    setOffset(offset + DEFAULT_PAGE_SIZE)
  }

  const updateFetchedData = useCallback(
    (newData: IArtwork[]) => {
      // setFetchedData(newData)

      if (fetchedData) {
        setFetchedData([...fetchedData, ...newData])
      } else {
        setFetchedData(newData)
      }
    },
    [fetchedData]
  )

  useEffect(() => {
    if (!data?.artworks) {
      return
    }

    updateFetchedData(data.artworks)
    setCanLoadMore(
      data.artworks.length !== 0 && data.artworks.length >= DEFAULT_PAGE_SIZE
    )
  }, [data])

  useEffect(() => {
    if (_.isEqual(queryParams, router.query)) {
      return
    }

    setQueryParams({ ...router.query })
    setLimit(DEFAULT_PAGE_SIZE)
    setOffset(0)
    setFetchedData(null)
  }, [router])

  const handleFilterChange = (options: FilterOptions) => {
    router.push({
      pathname: router.pathname,
      query: { ...options },
    })
  }

  return (
    <div>
      <Seo
        title={t("seo:search.title")}
        description={t("seo:search.description")}
      />
      <div className="px-4 sm:px-8 py-8">
        <ArtworkFilter onFilterChange={handleFilterChange} />
      </div>
      <br />
      {fetchedData && (
        <ArtworkGallery
          data={fetchedData}
          canLoadMore={canLoadMore}
          onLoadMore={handleLoadMore}
        />
      )}
      {isValidating && <LoadingView />}
      {error && <ErrorView error={error} />}
    </div>
  )
}

export default SearchPage
